// Geocentric sun/moon positions for placing them in a 3D Earth-centered scene.
//
// suncalc (MIT, https://github.com/mourner/suncalc) only exports observer-relative
// alt/az, not the geocentric equatorial coordinates a 3D scene needs. Its internal
// sunCoords()/moonCoords() (Meeus ch. 25 and ch. 47) are exactly that, so this
// reimplements them faithfully rather than guessing at different formulas — see
// the cross-check in celestial.test-notes.md for how this was verified against
// suncalc's own public API.
const { sin, cos, tan, asin, atan2: atan } = Math;
const rad = Math.PI / 180;

const J1970 = 2440588;
const J2000 = 2451545;

function toDays(date) {
  return date.valueOf() / 86400000 - 0.5 + J1970 - J2000;
}

function deltaT(d) {
  const y = 2000 + d / 365.2425;
  let t;
  if (y < 2005) {
    t = y - 2000;
    return 63.86 + t * (0.3345 + t * (-0.060374 + t * (0.0017275 + t * (0.000651814 + t * 0.00002373599))));
  }
  if (y < 2050) {
    t = y - 2000;
    return 62.92 + t * (0.32217 + t * 0.005589);
  }
  t = (y - 1820) / 100;
  return -20 + 32 * t * t;
}

function toDaysTT(d) {
  return d + deltaT(d) / 86400;
}

// Sun's apparent geocentric equatorial coordinates, Meeus ch. 25.
function sunCoords(d) {
  const t = d / 36525;
  const L0 = rad * (280.46646 + t * (36000.76983 + t * 0.0003032));
  const M = rad * (357.52911 + t * (35999.05029 - t * 0.0001537));
  const sinM = sin(M);
  const cosM = cos(M);
  const C = rad * (
    (1.914602 - t * (0.004817 + t * 0.000014)) * sinM +
    (0.019993 - 0.000101 * t) * 2 * sinM * cosM +
    0.000289 * sinM * (3 - 4 * sinM * sinM)
  );
  const Om = rad * (125.04 - 1934.136 * t);
  const L = L0 + C - rad * (0.00569 + 0.00478 * sin(Om));
  const e = rad * (23.439291 - t * 0.0130042) + rad * 0.00256 * cos(Om);
  return { ra: atan(cos(e) * sin(L), cos(L)), dec: asin(sin(e) * sin(L)), distAu: 1 };
}

// Moon's apparent geocentric equatorial coordinates, Meeus ch. 47 (truncated series).
const moonLon = [
  [0, 0, 1, 0, 6288774], [2, 0, -1, 0, 1274027], [2, 0, 0, 0, 658314], [0, 0, 2, 0, 213618],
  [0, 1, 0, 0, -185116], [0, 0, 0, 2, -114332], [2, 0, -2, 0, 58793], [2, -1, -1, 0, 57066],
  [2, 0, 1, 0, 53322], [2, -1, 0, 0, 45758], [0, 1, -1, 0, -40923], [1, 0, 0, 0, -34720],
  [0, 1, 1, 0, -30383], [2, 0, 0, -2, 15327], [0, 0, 1, 2, -12528], [0, 0, 1, -2, 10980],
  [4, 0, -1, 0, 10675], [0, 0, 3, 0, 10034], [4, 0, -2, 0, 8548], [2, 1, -1, 0, -7888],
  [2, 1, 0, 0, -6766], [1, 0, -1, 0, -5163], [1, 1, 0, 0, 4987], [2, -1, 1, 0, 4036],
  [2, 0, 2, 0, 3994], [4, 0, 0, 0, 3861], [2, 0, -3, 0, 3665], [0, 1, -2, 0, -2689],
  [2, 0, -1, 2, -2602], [2, -1, -2, 0, 2390], [1, 0, 1, 0, -2348], [2, -2, 0, 0, 2236],
  [0, 1, 2, 0, -2120], [0, 2, 0, 0, -2069], [2, -2, -1, 0, 2048], [2, 0, 1, -2, -1773],
  [2, 0, 0, 2, -1595], [4, -1, -1, 0, 1215], [0, 0, 2, 2, -1110], [3, 0, -1, 0, -892],
  [2, 1, 1, 0, -810], [4, -1, -2, 0, 759], [0, 2, -1, 0, -713], [2, 2, -1, 0, -700],
  [2, 1, -2, 0, 691], [2, -1, 0, -2, 596], [4, 0, 1, 0, 549], [0, 0, 4, 0, 537],
  [4, -1, 0, 0, 520], [1, 0, -2, 0, -487], [2, 1, 0, -2, -399], [0, 0, 2, -2, -381],
  [1, 1, 1, 0, 351], [3, 0, -2, 0, -340], [4, 0, -3, 0, 330], [2, -1, 2, 0, 327],
  [0, 2, 1, 0, -323], [1, 1, -1, 0, 299], [2, 0, 3, 0, 294], [2, 0, -1, -2, 0],
];
const moonLat = [
  [0, 0, 0, 1, 5128122],
  [0, 0, 1, 1, 280602],
  [0, 0, 1, -1, 277693],
  [2, 0, 0, -1, 173237],
  [2, 0, -1, 1, 55413],
  [2, 0, -1, -1, 46271],
  [2, 0, 0, 1, 32573],
  [0, 0, 2, 1, 17198],
  [2, 0, 1, -1, 9266],
  [0, 0, 2, -1, 8822],
  [2, -1, 0, -1, 8216],
  [2, 0, -2, -1, 4324],
  [2, 0, 1, 1, 4200],
  [2, 1, 0, -1, -3359],
  [2, -1, -1, 1, 2463],
  [2, -1, 0, 1, 2211],
  [2, -1, -1, -1, 2065],
  [0, 1, -1, -1, -1870],
  [4, 0, -1, -1, 1828],
  [0, 1, 0, 1, -1794],
  [0, 0, 0, 3, -1749],
  [0, 1, -1, 1, -1565],
  [1, 0, 0, 1, -1491],
  [0, 1, 1, 1, -1475],
  [0, 1, 1, -1, -1410],
  [0, 1, 0, -1, -1344],
  [1, 0, 0, -1, -1335],
  [0, 0, 3, 1, 1107],
  [4, 0, 0, -1, 1021],
  [4, 0, -1, 1, 833],
  [0, 0, 1, -3, 777],
  [4, 0, -2, 1, 671],
  [2, 0, 0, -3, 607],
  [2, 0, 2, -1, 596],
  [2, -1, 1, -1, 491],
  [2, 0, -2, 1, -451],
  [0, 0, 3, -1, 439],
  [2, 0, 2, 1, 422],
  [2, 0, -3, -1, 421],
  [2, 1, -1, 1, -366],
  [2, 1, 0, 1, -351],
  [4, 0, 0, 1, 331],
  [2, -1, 1, 1, 315],
  [2, -2, 0, -1, 302],
  [0, 0, 1, 3, -283],
  [2, 1, 1, -1, -229],
  [1, 1, 0, -1, 223],
  [1, 1, 0, 1, 223],
  [0, 1, -2, -1, -220],
  [2, 1, -1, -1, -220],
  [1, 0, 1, 1, -185],
  [2, -1, -2, -1, 181],
  [0, 1, 2, 1, -177],
  [4, 0, -2, -1, 176],
  [4, -1, -1, -1, 166],
  [1, 0, 1, -1, -164],
  [4, 0, 1, -1, 132],
  [1, 0, -1, -1, -119],
  [4, -1, 0, -1, 115],
  [2, -2, 0, 1, 107],
];

function moonCoords(d) {
  const t = d / 36525;
  const Lp = rad * (218.3164477 + 481267.88123421 * t);
  const D = rad * (297.8501921 + 445267.1114034 * t);
  const M = rad * (357.5291092 + 35999.0502909 * t);
  const Mp = rad * (134.9633964 + 477198.8675055 * t);
  const F = rad * (93.272095 + 483202.0175233 * t);
  const E = 1 - t * 0.002516;

  let sl = 0;
  let sb = 0;
  for (const [d0, m, mp, f, coeff] of moonLon) {
    const arg = d0 * D + m * M + mp * Mp + f * F;
    const factor = Math.abs(m) === 1 ? E : Math.abs(m) === 2 ? E * E : 1;
    sl += coeff * factor * sin(arg);
  }
  for (const [d0, m, mp, f, coeff] of moonLat) {
    const arg = d0 * D + m * M + mp * Mp + f * F;
    const factor = Math.abs(m) === 1 ? E : Math.abs(m) === 2 ? E * E : 1;
    sb += coeff * factor * sin(arg);
  }
  sl += 3958 * sin(rad * (119.75 + 131.849 * t));
  sb += -2235 * sin(Lp) + 382 * sin(rad * (313.45 + 481266.484 * t));

  const l = Lp + rad * (sl / 1e6);
  const b = rad * (sb / 1e6);
  const e = rad * 23.439291;
  return {
    ra: atan(sin(l) * cos(e) - tan(b) * sin(e), cos(l)),
    dec: asin(sin(b) * cos(e) + cos(b) * sin(e) * sin(l)),
    distKm: 385000.56,
  };
}

/** Geocentric equatorial (ra/dec, radians) + distance for the Sun at `date`. */
export function sunGeocentric(date) {
  const d = toDaysTT(toDays(date));
  const c = sunCoords(d);
  return { ra: c.ra, dec: c.dec, distKm: c.distAu * 149597870.7 };
}

/** Geocentric equatorial (ra/dec, radians) + distance for the Moon at `date`. */
export function moonGeocentric(date) {
  const d = toDaysTT(toDays(date));
  const c = moonCoords(d);
  return { ra: c.ra, dec: c.dec, distKm: c.distKm };
}

/**
 * Converts geocentric ra/dec (radians) + a Greenwich Mean Sidereal Time (radians,
 * from satellite.js's gstime()) into Earth-fixed Cartesian km — the sub-point
 * longitude is ra - gmst (same relation as the hour-angle formula used for sky
 * look-angles, solved for the observer longitude where the object is at zenith).
 */
export function equatorialToEcf(ra, dec, distKm, gmstRad) {
  const lon = ra - gmstRad;
  return {
    x: distKm * cos(dec) * cos(lon),
    y: distKm * cos(dec) * sin(lon),
    z: distKm * sin(dec),
  };
}

/**
 * Converts geocentric ra/dec (radians) + distance into inertial (non-Earth-
 * rotating) equatorial Cartesian km — the same frame family satellite.js's
 * propagate() returns satellite positions in (TEME). The two frames differ
 * from true equatorial mean-of-date by only arcseconds, far tighter than an
 * eclipse/shadow geometry check against Earth's ~6371km radius needs.
 */
export function equatorialToEci(ra, dec, distKm) {
  return {
    x: distKm * cos(dec) * cos(ra),
    y: distKm * cos(dec) * sin(ra),
    z: distKm * sin(dec),
  };
}
